import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useState, useEffect, useCallback, useMemo, useImperativeHandle, useContext, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { isLoggedIn } from '../auth/authUtils.js';
import { LogSentryException } from '../sentryLogger/sentryLogger.js';
import { useBaseTracker } from '../telemetry/BaseTrackingAdapter.js';

const remoteConfigTimeoutMS = 3000;
const RemoteConfigContext = createContext({
    remoteConfig: {},
    remoteConfigClient: undefined,
    initialLoadFinished: false,
    isLoading: true,
});
const useInitialFetch = (identify) => {
    const tracker = useBaseTracker();
    const initialLoadStartedRef = useRef(false);
    useEffect(() => {
        if (!isLoggedIn() && !initialLoadStartedRef.current) {
            const fetch = async () => {
                initialLoadStartedRef.current = true;
                try {
                    await Promise.race([
                        identify(tracker.getTrackingID()),
                        new Promise((resolve) => setTimeout(resolve, remoteConfigTimeoutMS, "timeout")),
                    ]);
                }
                catch (error) {
                    LogSentryException({ messagePrefix: "Remote config error", error });
                }
            };
            fetch();
        }
    }, [identify, tracker]);
};
const RemoteConfigProvider = forwardRef(({ children, remoteConfigClient, }, ref) => {
    const tracker = useBaseTracker();
    const [remoteConfig, setRemoteConfig] = useState(remoteConfigClient.getAllConfig() ?? {});
    useEffect(() => {
        Sentry.setContext("experiments", remoteConfig);
        tracker.setExperiments(remoteConfig);
    }, [remoteConfig, tracker]);
    const fetchRemoteConfig = useCallback(async (fetchFunction) => {
        const startTime = performance.now();
        await fetchFunction();
        const elapsedTime = performance.now() - startTime;
        const config = remoteConfigClient.getAllConfig() ?? {};
        tracker.setExperiments(config);
        setRemoteConfig(remoteConfigClient.getAllConfig() ?? {});
        tracker.trackRemoteConfigReceived({ duration: Math.round(elapsedTime) });
    }, [tracker, remoteConfigClient]);
    const [initialLoadFinished, setInitialLoadFinished] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const identify = useCallback(async (userId, traits) => {
        setIsLoading(true);
        await fetchRemoteConfig(remoteConfigClient.identify.bind(remoteConfigClient, userId, traits));
        setInitialLoadFinished(true);
        setIsLoading(false);
    }, [fetchRemoteConfig, remoteConfigClient]);
    const remoteConfigReactClient = useMemo(() => ({ identify, setIsLoading }), [identify]);
    useImperativeHandle(ref, () => remoteConfigReactClient, [remoteConfigReactClient]);
    useInitialFetch(identify);
    return (jsx(RemoteConfigContext.Provider, { value: {
            remoteConfigClient: remoteConfigReactClient,
            remoteConfig,
            initialLoadFinished,
            isLoading,
        }, children: children }));
});
const useRemoteConfigContext = () => {
    return useContext(RemoteConfigContext);
};
const useRemoteConfig = (configName) => {
    const { remoteConfig, initialLoadFinished } = useContext(RemoteConfigContext);
    if (!initialLoadFinished)
        return undefined;
    return remoteConfig[configName] ?? (configName.endsWith("-ff") ? false : "control");
};
RemoteConfigProvider.displayName = "RemoteConfigProvider";

export { RemoteConfigContext, RemoteConfigProvider, remoteConfigTimeoutMS, useRemoteConfig, useRemoteConfigContext };
//# sourceMappingURL=RemoteConfigProvider.js.map
